import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import {
  emptyResponseSchema,
  SecurityError,
  securityErrorKey,
  useSensitiveRequest,
} from '@/features/security/securityClient'
import type { FactorMethod } from '@/features/security/securityClient'
import { FactorFields } from '@/features/security/SecurityFields'
import { useCheckTokenGuard } from '@/hooks/useCheckTokenGuard'
import type { createPasswordType } from '@/utils/schemas'
import { createPasswordSchema } from '@/utils/schemas'

export const CreatePasswordForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [token] = useState(() =>
    new URLSearchParams(location.search).get('token'),
  )
  useEffect(() => {
    window.history.replaceState(
      window.history.state,
      '',
      AppRoutes.createPassword,
    )
  }, [])

  useCheckTokenGuard(token)

  const form = useForm<createPasswordType>({
    resolver: zodResolver(createPasswordSchema),
    defaultValues: {
      createPassword: '',
      confirmPassword: '',
    },
  })

  const {
    handleSubmit,
    control,
    formState: { errors = {}, dirtyFields },
    watch,
  } = form

  const request = useSensitiveRequest()
  const [isLoading, setIsLoading] = useState(false)
  const [needsFactor, setNeedsFactor] = useState(false)
  const [method, setMethod] = useState<FactorMethod>('totp')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const lock = useRef(false)

  const changePasswordRequest = async (token: string, newPassword: string) => {
    if (lock.current) return
    lock.current = true
    setIsLoading(true)
    setError('')
    try {
      await request('/api/v1/auth/change-password', emptyResponseSchema, {
        body: {
          newPassword,
          token,
          ...(needsFactor ? { method, code: code.trim() } : {}),
        },
      })
      form.reset()
      setCode('')
      toast.success(t('feedback.toast.passwordChanged'))
      navigate(AppRoutes.login)
    } catch (e) {
      setCode('')
      if (e instanceof SecurityError && e.code === 'MFA_REQUIRED')
        setNeedsFactor(true)
      setError(
        e instanceof SecurityError && e.code === 'MFA_REQUIRED'
          ? 'security.resetMfaRequired'
          : securityErrorKey(e),
      )
    } finally {
      lock.current = false
      setIsLoading(false)
    }
  }

  const passNotMatch =
    Object.keys(dirtyFields).includes('confirmPassword') &&
    watch().confirmPassword !== watch().createPassword

  const onSubmit = (data: any) => {
    if (passNotMatch) {
      return
    } else {
      changePasswordRequest(token as string, data.createPassword)
    }
  }

  return (
    <div className="flex flex-col gap-8 w-full max-w-[436px]">
      <div className="flex flex-col gap-5">
        <p className="text-[40px] font-bold tracking-wide">
          {t('dashboard.landingCreatePassword.createPassword')}
        </p>
        <p className="text-1xl text-textAccent tracking-tight">
          {t('dashboard.landingCreatePassword.welcomeBack')}
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="sentry-block flex flex-col gap-6"
          data-sentry-block="true"
        >
          {error && (
            <p role="alert" className="text-dynamicRed">
              {t(error)}
            </p>
          )}
          <FormField
            control={control}
            name="createPassword"
            render={({ field }) => (
              <FormItem className="w-full bg-white rounded-md">
                <FormLabel>{t('security.newPassword')}</FormLabel>
                <FormControl className="w-full focus:outline-none">
                  <Input
                    {...field}
                    placeholder={t('security.newPassword')}
                    aria-label={t('security.newPassword')}
                    autoComplete="new-password"
                    type="password"
                    showPassIcon
                  />
                </FormControl>
                <FormMessage data-name="createPassword" />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="w-full bg-white rounded-md">
                <FormLabel>{t('security.confirmPassword')}</FormLabel>
                <FormControl className="w-full focus:outline-none">
                  <Input
                    {...field}
                    placeholder={t('security.confirmPassword')}
                    aria-label={t('security.confirmPassword')}
                    autoComplete="new-password"
                    type="password"
                    showPassIcon
                  />
                </FormControl>
                {passNotMatch && !errors.confirmPassword && (
                  <p className="text-[0.8rem] font-medium text-destructive m-">
                    {t('dashboard.landingCreatePassword.passwordsNotMatch')}
                  </p>
                )}
                <FormMessage data-name="confirmPassword" />
              </FormItem>
            )}
          />

          {needsFactor && (
            <FactorFields
              method={method}
              code={code}
              onCode={setCode}
              disabled={isLoading}
              onMethod={(next) => {
                setMethod(next)
                setCode('')
              }}
            />
          )}
          <Button
            type="submit"
            className="py-3 px-5 font-medium tracking-widest h-11"
            isLoading={isLoading}
          >
            {t('dashboard.landingCreatePassword.createPasswordButton')}
          </Button>
        </form>
      </Form>
    </div>
  )
}
